import axios from 'axios';

declare global {
    interface Window {
        __DASHBAL_CONFIG__?: {
            apiBaseUrl?: string;
        };
    }
}

// Get API base URL from runtime config (injected by server.js)
// In production: https://api.dashbal.com
// In development: http://localhost:3001
const getApiBaseUrl = (): string => {
    // 1. Runtime config injected by server.js (Production & Docker)
    if (typeof window !== 'undefined' && window.__DASHBAL_CONFIG__?.apiBaseUrl) {
        return window.__DASHBAL_CONFIG__.apiBaseUrl;
    }

    // 2. Build-time environment variable (esbuild define)
    if (process.env.API_BASE_URL) {
        return process.env.API_BASE_URL;
    }

    // 3. Development Fallback
    if (process.env.NODE_ENV !== 'production') {
        return 'http://localhost:3001';
    }

    // 4. Production Fallback
    console.warn('API URL not configured. Defaulting to current origin.');
    return window.location.origin;
};

// Create a separate axios instance for maintenance endpoints
const maintenanceClient = axios.create({
    baseURL: getApiBaseUrl(),
    timeout: 10000,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
});

export interface MaintenanceStatus {
    maintenanceMode: boolean;
    hasAccess: boolean;
}

export interface MaintenanceVerifyResponse {
    message: string;
    expiresIn: string;
}

export async function getMaintenanceStatus(): Promise<MaintenanceStatus> {
    try {
        const response = await maintenanceClient.get('/maintenance/status');
        if (response.data.success) {
            return response.data.data;
        }
        throw new Error('Failed to get maintenance status');
    } catch (error: any) {
        console.error('Failed to check maintenance status:', error);
        return {
            maintenanceMode: false,
            hasAccess: true,
        };
    }
}

export async function verifyMaintenanceCode(code: string): Promise<MaintenanceVerifyResponse> {
    const response = await maintenanceClient.post('/maintenance/verify', { code });
    if (response.data.success) {
        return response.data.data;
    }
    throw new Error(response.data.error || 'Verification failed');
}

export async function revokeMaintenanceAccess(): Promise<void> {
    await maintenanceClient.post('/maintenance/revoke');
}
